/**
 * Maps i18n language codes to date-fns locale objects
 * Supports: en, es, fr, pt
 */

import {enUS, es, fr, ptBR} from 'date-fns/locale';

type LocaleCode = 'en' | 'es' | 'fr' | 'pt';

/**
 * Maps i18n language code to date-fns locale object
 * @param language - Language code (en, es, fr, pt)
 * @returns date-fns locale object
 * @default enUS if language not found
 */
export const getDateFnsLocale = (language: LocaleCode) => {
  const localeMap: Record<LocaleCode, Locale> = {
    en: enUS,
    es: es,
    fr: fr,
    pt: ptBR,
  };

  return localeMap[language] || enUS;
};

/**
 * Gets the date format pattern for a given language
 * Used for formatting dates according to regional preferences
 * @param language - Language code (en, es, fr, pt)
 * @returns Date format string (used by date-fns or similar)
 */
export const getDateFormatPattern = (language: LocaleCode): string => {
  const formatMap: Record<LocaleCode, string> = {
    en: 'dd/MM/yyyy', // US format: 25/12/2026
    es: 'dd/MM/yyyy', // Spanish: 25/12/2026
    fr: 'dd/MM/yyyy', // French: 25/12/2026
    pt: 'dd/MM/yyyy', // Portuguese: 25/12/2026
  };

  return formatMap[language] || 'dd/MM/yyyy';
};

/**
 * Gets MUI DatePicker locale text overrides for a given language
 * This is used to customize month/day names in MUI DatePicker
 * @param language - Language code (en, es, fr, pt)
 * @returns Partial DatePickerLocaleText config for MUI
 */
export const getMuiDateLocaleText = (language: LocaleCode) => {
  const localeTextMap: Record<
    LocaleCode,
    {todayButtonLabel: string; cancelButtonLabel: string; okButtonLabel: string}
  > = {
    en: {
      todayButtonLabel: 'Today',
      cancelButtonLabel: 'Cancel',
      okButtonLabel: 'OK',
    },
    es: {
      todayButtonLabel: 'Hoy',
      cancelButtonLabel: 'Cancelar',
      okButtonLabel: 'Aceptar',
    },
    fr: {
      todayButtonLabel: "Aujourd'hui",
      cancelButtonLabel: 'Annuler',
      okButtonLabel: 'OK',
    },
    pt: {
      todayButtonLabel: 'Hoje',
      cancelButtonLabel: 'Cancelar',
      okButtonLabel: 'OK',
    },
  };

  return localeTextMap[language] || localeTextMap.en;
};
